/**
 * Commission Adjustments Table - Reusable component for managing commission adjustments
 * Can be used in both:
 * 1. Overview page - Shows all brands, full page layout
 * 2. Brand detail page - Shows single brand, embedded in tabs
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  Banner,
  Button,
  DataTable,
  EmptyState,
  Tooltip,
  Icon,
  Modal,
  FormLayout,
  TextField,
  Select,
  TextContainer,
  SkeletonDisplayText,
  SkeletonBodyText,
  Pagination,
  Filters,
  ChoiceList,
  DatePicker,
  Popover,
  ActionList,
} from '@shopify/polaris';
import {
  CashDollarIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon,
  SearchIcon,
  FilterIcon,
  ExportIcon,
  ViewIcon,
} from '@shopify/polaris-icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  useCommissionAdjustments,
  usePendingAdjustments,
  useCreateCommissionAdjustment,
  useUpdateCommissionAdjustment,
  useApproveCommissionAdjustment,
  useRejectCommissionAdjustment,
  useDeleteCommissionAdjustment,
  useCalculateAdjustmentImpact,
  useAdjustmentAuditTrail,
  useBrands,
} from '../../utils/queries';
import { validateNumeric, sanitizeTextInput } from '../../utils/inputSecurity';
import apiClient from '../../utils/api';
import { PAGE_SIZE_OPTIONS, PageSizeSelect } from '../../utils/table';

// Constants matching backend enums
const ADJUSTMENT_TYPES = [
  { label: 'Addition', value: 'ADDITION' },
  { label: 'Deduction', value: 'DEDUCTION' },
];

const APPROVAL_STATUS_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
];

/**
 * The sizes the shared control can actually show, as numbers.
 *
 * `?limit=` is part of the URL, so it can arrive as ANY number — from an old shared link, or
 * typed by hand. Polaris `Select` falls back to the FIRST option when its value matches none
 * of them (Select.js getSelectedOption), so rendering the shared control against an unlisted
 * size would label a 17-row page "5". Where the active size is not one of these, the footer
 * states the real number instead and offers a listed size to move to.
 */
const OFFERED_PAGE_SIZES = new Set(PAGE_SIZE_OPTIONS.map((o) => Number(o.value)));

/**
 * What the table opens on when the URL names no `limit` — the count this table has always
 * used, and one of PAGE_SIZE_OPTIONS, so the rows-per-page control can show it. A `?limit=`
 * already in a link is honoured verbatim.
 */
const DEFAULT_LIMIT = 20;

export default function CommissionAdjustmentsTable({
  brandId = null,           // null = show all brands, specific ID = filter to that brand
  showBrandColumn = true,   // Show brand column in table (false for brand-specific view)
  title = 'Commission Adjustments',
  subtitle = 'Manage manual commission adjustments',
  embedded = false,         // true = Card wrapper (for tabs), false = Page wrapper
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State for filters and pagination
  const [filters, setFilters] = useState({
    brandId: brandId || searchParams.get('brandId') || '',
    appliedMonth: searchParams.get('appliedMonth') || '',
    approvalStatus: searchParams.get('approvalStatus') || '',
    search: searchParams.get('search') || '',
  });

  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: parseInt(searchParams.get('limit')) || DEFAULT_LIMIT,
  });

  /**
   * The row count goes through the SAME state the request and the URL already read.
   *
   * This table pages server-side — `limit` is sent to GET /commission-adjustments and the
   * response's own pagination drives the pager — so the chosen size has to be the API's limit.
   * Slicing the returned array would show 5 of the 25 rows the server sent while labelling it
   * "Page 1 of 5". Writing it into `pagination` re-fires the query through `queryParams` and
   * lands in `?limit=` through the URL effect below, so the URL stays the source of truth.
   *
   * Page 1, always: a new size makes the old page number meaningless, and on the last page it
   * would ask the server for a page that no longer exists.
   */
  const handleLimitChange = useCallback((next) => {
    setPagination({ page: 1, limit: next });
  }, []);

  // Auto-set brandId filter when prop provided
  useEffect(() => {
    if (brandId) {
      setFilters(prev => ({ ...prev, brandId }));
    }
  }, [brandId]);

  // Modal states
  const [createModalActive, setCreateModalActive] = useState(false);
  const [editModalActive, setEditModalActive] = useState(false);
  const [auditModalActive, setAuditModalActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [impactModalActive, setImpactModalActive] = useState(false);
  // Which form the impact preview was opened FROM, so closing it can put that form back.
  // Without this, previewing the impact was a one-way trip: the form closed, and the only
  // route back to it reset every field first.
  const [impactReturnTo, setImpactReturnTo] = useState(null); // 'create' | 'edit' | null
  const [rejectModalActive, setRejectModalActive] = useState(false);

  // Form states
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [formData, setFormData] = useState({
    brandId: brandId || '',
    adjustmentType: 'ADDITION',
    amount: '',
    reason: '',
    appliedMonth: '',
    externalRef: '',
  });

  const [deleteReason, setDeleteReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [impactData, setImpactData] = useState(null);
  const [submitError, setSubmitError] = useState('');

  // Data fetching with current filters
  const queryParams = useMemo(() => ({
    ...filters,
    ...pagination,
  }), [filters, pagination]);

  const {
    data: adjustmentsResponse,
    isLoading: adjustmentsLoading,
    error: adjustmentsError,
    refetch: refetchAdjustments,
  } = useCommissionAdjustments(queryParams);

  const {
    data: pendingResponse,
    isLoading: pendingLoading,
  } = usePendingAdjustments();

  const {
    data: brandsResponse,
    isLoading: brandsLoading,
  } = useBrands();

  const {
    data: auditTrailResponse,
    isLoading: auditLoading,
  } = useAdjustmentAuditTrail(selectedAdjustment?.id, {
    enabled: !!selectedAdjustment?.id && auditModalActive,
  });

  // Mutations
  const createMutation = useCreateCommissionAdjustment();
  const updateMutation = useUpdateCommissionAdjustment();
  const approveMutation = useApproveCommissionAdjustment();
  const rejectMutation = useRejectCommissionAdjustment();
  const deleteMutation = useDeleteCommissionAdjustment();
  const impactMutation = useCalculateAdjustmentImpact();

  // Extract data safely
  const adjustments = adjustmentsResponse?.adjustments || [];
  const adjustmentsPagination = adjustmentsResponse?.pagination || {};
  const pendingAdjustments = pendingResponse?.adjustments || [];
  const brands = brandsResponse?.brands || brandsResponse || [];
  const auditTrail = auditTrailResponse?.auditTrail || [];

  // Update URL when filters/pagination change (only if not embedded)
  useEffect(() => {
    if (!embedded) {
      const params = new URLSearchParams();
      Object.entries({ ...filters, ...pagination }).forEach(([key, value]) => {
        if (value && key !== 'brandId') params.set(key, value); // Don't persist brandId when embedded
      });
      setSearchParams(params, { replace: true });
    }
  }, [filters, pagination, setSearchParams, embedded]);

  // Generate month options (last 12 months)
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ label: monthLabel, value: monthString });
    }
    return options;
  }, []);

  // Brand options for form
  const brandOptions = useMemo(() => {
    return brands.map(brand => ({
      label: brand.name,
      value: brand.id,
    }));
  }, [brands]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      brandId: brandId || '',
      adjustmentType: 'ADDITION',
      amount: '',
      reason: '',
      appliedMonth: '',
      externalRef: '',
    });
    setFormErrors({});
    setImpactData(null);
    setSubmitError('');
  }, [brandId]);

  // Validate form
  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.brandId) errors.brandId = 'Brand is required';
    if (!formData.amount) {
      errors.amount = 'Amount is required';
    } else if (!validateNumeric(formData.amount) || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Amount must be a positive number';
    }
    if (!formData.reason.trim()) errors.reason = 'Reason is required';
    if (!formData.appliedMonth) errors.appliedMonth = 'Applied month is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    try {
      const sanitizedData = {
        ...formData,
        amount: parseFloat(formData.amount),
        reason: sanitizeTextInput(formData.reason),
        externalRef: sanitizeTextInput(formData.externalRef),
      };

      if (selectedAdjustment) {
        await updateMutation.mutateAsync({
          adjustmentId: selectedAdjustment.id,
          updateData: sanitizedData,
        });
        setEditModalActive(false);
      } else {
        await createMutation.mutateAsync(sanitizedData);
        setCreateModalActive(false);
      }

      resetForm();
      setSelectedAdjustment(null);
      refetchAdjustments();
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(error?.response?.data?.message || error?.message || 'Failed to save adjustment');
    }
  }, [formData, selectedAdjustment, validateForm, updateMutation, createMutation, resetForm, refetchAdjustments]);

  // Handle approval
  const handleApprove = useCallback(async (adjustmentId) => {
    try {
      await approveMutation.mutateAsync(adjustmentId);
      refetchAdjustments();
      if (filters.approvalStatus === 'PENDING') {
        setFilters(prev => ({ ...prev, approvalStatus: '' }));
      }
    } catch (error) {
      console.error('Approval error:', error);
    }
  }, [approveMutation, refetchAdjustments, filters.approvalStatus]);

  // Handle reject
  const handleReject = useCallback(async () => {
    if (!selectedAdjustment) return;

    try {
      await rejectMutation.mutateAsync({
        adjustmentId: selectedAdjustment.id,
        reason: sanitizeTextInput(rejectReason),
      });
      setRejectModalActive(false);
      setSelectedAdjustment(null);
      setRejectReason('');
      refetchAdjustments();
      if (filters.approvalStatus === 'PENDING') {
        setFilters(prev => ({ ...prev, approvalStatus: '' }));
      }
    } catch (error) {
      console.error('Rejection error:', error);
    }
  }, [selectedAdjustment, rejectReason, rejectMutation, refetchAdjustments, filters.approvalStatus]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!selectedAdjustment) return;

    try {
      await deleteMutation.mutateAsync({
        adjustmentId: selectedAdjustment.id,
        reason: sanitizeTextInput(deleteReason),
      });
      setDeleteModalActive(false);
      setSelectedAdjustment(null);
      setDeleteReason('');
      refetchAdjustments();
    } catch (error) {
      console.error('Delete error:', error);
    }
  }, [selectedAdjustment, deleteReason, deleteMutation, refetchAdjustments]);

  // Calculate impact
  const handleCalculateImpact = useCallback(async () => {
    if (!formData.brandId || !formData.appliedMonth || !formData.amount) return;

    try {
      const proposedAdjustment = {
        adjustmentType: formData.adjustmentType,
        amount: parseFloat(formData.amount),
      };

      const response = await impactMutation.mutateAsync({
        brandId: formData.brandId,
        appliedMonth: formData.appliedMonth,
        proposedAdjustment,
      });

      setImpactData(response);
      // Close the form first. Leaving it open stacked two Polaris Modals on top of one
      // another, which traps focus in the lower one and makes Escape ambiguous.
      //
      // Remember which form it was, so closing the preview restores it with the entered
      // values intact. Previously only createModalActive was cleared, which meant this had
      // two faults at once: opened from Create the form was closed and never came back —
      // and every route back to it calls resetForm(), so the whole form had to be retyped —
      // while opened from EDIT the flag was left set, so the preview stacked on top of the
      // still-open edit modal, which is the exact situation the line above avoids.
      setImpactReturnTo(editModalActive ? 'edit' : 'create');
      setCreateModalActive(false);
      setEditModalActive(false);
      setImpactModalActive(true);
    } catch (error) {
      console.error('Impact calculation error:', error);
    }
  }, [formData, impactMutation, editModalActive]);

  /**
   * Leave the impact preview and go back to the form it was opened from.
   *
   * Used by BOTH the primary action and onClose: dismissing with Escape, the X, or the
   * backdrop has to restore the form too, or those three routes still lose the entered
   * values while the button works.
   */
  const closeImpactPreview = useCallback(() => {
    setImpactModalActive(false);
    if (impactReturnTo === 'edit') setEditModalActive(true);
    else if (impactReturnTo === 'create') setCreateModalActive(true);
    setImpactReturnTo(null);
  }, [impactReturnTo]);

  // Table data transformation
  const tableRows = useMemo(() => {
    return adjustments.map((adjustment) => {
      const statusBadge = adjustment.approvalStatus === 'APPROVED'
        ? <Badge tone="success">Approved</Badge>
        : adjustment.approvalStatus === 'PENDING'
        ? <Badge tone="attention">Pending</Badge>
        : <Badge tone="critical">Rejected</Badge>;

      const typeBadge = adjustment.adjustmentType === 'ADDITION'
        ? <Badge tone="success">+{apiClient.formatCurrency(adjustment.amount)}</Badge>
        : <Badge tone="critical">-{apiClient.formatCurrency(adjustment.amount)}</Badge>;

      const baseRow = [
        <Text variant="bodyMd">{adjustment.appliedMonth}</Text>,
      ];

      // Conditionally add brand column
      if (showBrandColumn) {
        baseRow.push(<Text variant="bodyMd" fontWeight="medium">{adjustment.brand?.name || 'Unknown'}</Text>);
      }

      // Order is Month, [Brand], Amount, Status, Reason, Created, Actions. Status sits beside
      // Amount because the two are read together — an amount you cannot act on until it is
      // approved — and Reason is free text of variable length, so it belongs after the short
      // fixed-width columns rather than between them. tableHeaders must stay in this order.
      baseRow.push(
        typeBadge,
        statusBadge,
        <Text variant="bodyMd" truncate>{adjustment.reason}</Text>,
        <Text variant="bodySm" tone="subdued">{apiClient.formatDate(adjustment.createdAt)}</Text>,
        // wrap={false}: InlineStack defaults to flex-wrap: wrap, which stacked the action
        // icons one-per-line whenever the cell was squeezed. The row icons are a single
        // control group and must stay on one line; the table scrolls instead.
        <InlineStack gap="200" wrap={false}>
          <Tooltip content="View audit trail">
            <Button
              variant="tertiary"
              size="micro"
              icon={ViewIcon}
              onClick={() => {
                setSelectedAdjustment(adjustment);
                setAuditModalActive(true);
              }}
            />
          </Tooltip>
          <Tooltip content="Edit adjustment">
            <Button
              variant="tertiary"
              size="micro"
              icon={EditIcon}
              onClick={() => {
                setSelectedAdjustment(adjustment);
                setFormData({
                  brandId: adjustment.brandId,
                  adjustmentType: adjustment.adjustmentType,
                  amount: adjustment.amount.toString(),
                  reason: adjustment.reason,
                  appliedMonth: adjustment.appliedMonth,
                  externalRef: adjustment.externalRef || '',
                });
                setEditModalActive(true);
              }}
            />
          </Tooltip>
          {adjustment.approvalStatus === 'PENDING' && (
            <>
              <Tooltip content="Approve adjustment">
                <Button
                  variant="tertiary"
                  size="micro"
                  icon={CheckCircleIcon}
                  onClick={() => handleApprove(adjustment.id)}
                  loading={approveMutation.isPending}
                />
              </Tooltip>
              <Tooltip content="Reject adjustment">
                <Button
                  variant="tertiary"
                  size="micro"
                  icon={AlertTriangleIcon}
                  tone="critical"
                  onClick={() => {
                    setSelectedAdjustment(adjustment);
                    setRejectModalActive(true);
                  }}
                  loading={rejectMutation.isPending}
                />
              </Tooltip>
            </>
          )}
          <Tooltip content="Delete adjustment">
            <Button
              variant="tertiary"
              size="micro"
              icon={DeleteIcon}
              tone="critical"
              onClick={() => {
                setSelectedAdjustment(adjustment);
                setDeleteModalActive(true);
              }}
            />
          </Tooltip>
        </InlineStack>
      );

      return baseRow;
    });
  }, [adjustments, showBrandColumn, approveMutation.isPending, rejectMutation.isPending, handleApprove]);

  const tableHeaders = useMemo(() => {
    const headers = ['Month'];
    if (showBrandColumn) headers.push('Brand');
    headers.push('Amount', 'Status', 'Reason', 'Created', 'Actions');
    return headers;
  }, [showBrandColumn]);

  // Filter JSX
  const appliedFilters = Object.entries(filters)
    .filter(([key, value]) => value && (key !== 'brandId' || !brandId)) // Hide brandId filter when prop provided
    .map(([key, value]) => {
      let label = key;
      let displayValue = value;

      if (key === 'brandId') {
        const brand = brands.find(b => b.id === value);
        label = 'Brand';
        displayValue = brand?.name || value;
      } else if (key === 'approvalStatus') {
        const status = APPROVAL_STATUS_OPTIONS.find(s => s.value === value);
        label = 'Status';
        displayValue = status?.label || value;
      } else if (key === 'appliedMonth') {
        const month = monthOptions.find(m => m.value === value);
        label = 'Month';
        displayValue = month?.label || value;
      }

      return {
        key,
        label,
        value: displayValue,
        onRemove: () => handleFilterRemove(key),
      };
    });

  const handleFilterRemove = useCallback((key) => {
    setFilters(prev => ({
      ...prev,
      [key]: key === 'brandId' && brandId ? brandId : ''
    }));
  }, [brandId]);

  const handleFiltersClearAll = useCallback(() => {
    setFilters({
      brandId: brandId || '',
      appliedMonth: '',
      approvalStatus: '',
      search: '',
    });
  }, [brandId]);

  // Loading state
  if (adjustmentsLoading && pagination.page === 1) {
    const loadingContent = (
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <SkeletonDisplayText size="medium" />
            <SkeletonBodyText lines={4} />
          </BlockStack>
        </Box>
      </Card>
    );

    if (embedded) {
      return loadingContent;
    }

    return (
      <Page title={title} subtitle={subtitle} fullWidth>
        <Layout>
          <Layout.Section>
            {loadingContent}
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Main content (filters and modals excluded to keep within length limits - will add in next message)
  const mainContent = (
    <>
      <Card>
        <Box padding="200">
          <BlockStack gap="300">
            {/* Header inside the card when embedded.

                It used to sit above the Card, so the title, the subtitle and New Adjustment
                floated on the page background while the thing they describe had its own frame
                below them. Gated on `embedded` because the Page branch already renders title and
                subtitle in the Page header — unconditional would print both twice. */}
            {embedded && (
              <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
                <BlockStack gap="050">
                  <Text variant="headingMd">{title}</Text>
                  <Text variant="bodySm" tone="subdued">{subtitle}</Text>
                </BlockStack>
                <Button
                  variant="primary"
                  icon={PlusIcon}
                  onClick={() => {
                    resetForm();
                    setCreateModalActive(true);
                  }}
                >
                  New Adjustment
                </Button>
              </InlineStack>
            )}
            {/* Filters */}
            <Filters
              queryValue={filters.search}
              queryPlaceholder="Search by reason..."
              filters={[
                // Only show brand filter if not filtering by brandId prop
                ...(brandId ? [] : [{
                  key: 'brandId',
                  label: 'Brand',
                  filter: (
                    <ChoiceList
                      title="Brand"
                      choices={brandOptions}
                      selected={filters.brandId ? [filters.brandId] : []}
                      onChange={(selected) =>
                        setFilters(prev => ({ ...prev, brandId: selected[0] || '' }))
                      }
                    />
                  ),
                }]),
                {
                  key: 'appliedMonth',
                  label: 'Month',
                  filter: (
                    <ChoiceList
                      title="Applied Month"
                      choices={monthOptions}
                      selected={filters.appliedMonth ? [filters.appliedMonth] : []}
                      onChange={(selected) =>
                        setFilters(prev => ({ ...prev, appliedMonth: selected[0] || '' }))
                      }
                    />
                  ),
                },
                {
                  key: 'approvalStatus',
                  label: 'Status',
                  filter: (
                    <Select
                      label="Status"
                      labelHidden
                      options={APPROVAL_STATUS_OPTIONS}
                      value={filters.approvalStatus}
                      onChange={(value) =>
                        setFilters(prev => ({ ...prev, approvalStatus: value }))
                      }
                    />
                  ),
                },
              ]}
              appliedFilters={appliedFilters}
              onQueryChange={(value) => setFilters(prev => ({ ...prev, search: value }))}
              onQueryClear={() => setFilters(prev => ({ ...prev, search: '' }))}
              onClearAll={handleFiltersClearAll}
            />

            {/* Data Table */}
            <>
                {/* Every column 'text'. Amount was 'numeric', which right-aligns it — so on a
                    wide card the +$5.00 badge sat at the far edge of its column with a gulf
                    between it and Month, and the row read as two disconnected halves.

                    THE TABLE IS RENDERED WITH NO ROWS TOO. It used to be replaced by the
                    EmptyState, so a brand with no adjustments — or a filter that matched
                    none — saw no columns and no sign of what an adjustment records. DataTable
                    has no `itemCount` or `emptyState`, but it derives a cell's colSpan from
                    the row's length against headings.length (DataTable.js getColSpan), so a
                    one-cell row spans the table and carries the message instead. */}
                <DataTable
                  columnContentTypes={tableHeaders.map(() => 'text')}
                  headings={tableHeaders.map((h) => (
                    <Text key={h} variant="bodyMd" fontWeight="semibold" as="span">{h}</Text>
                  ))}
                  rows={adjustments.length === 0 ? [[
                    <EmptyState
                      key="no-adjustments"
                      heading="No commission adjustments found"
                      action={{
                        content: 'Create First Adjustment',
                        onAction: () => {
                          resetForm();
                          setCreateModalActive(true);
                        },
                      }}
                      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                    >
                      <p>Create manual commission adjustments to handle special cases, goodwill credits, or processing fees.</p>
                    </EmptyState>,
                  ]] : tableRows}
                  loading={adjustmentsLoading}
                />

                {/* Rows per page and the pager in one row. The pager drops out when
                    everything fits; the row count stays, since it is how someone gets a
                    second page at all. Neither is shown over an empty table: there is no
                    page to size. */}
                {adjustments.length > 0 && (
                <Box paddingBlockStart="400">
                  <InlineStack align="space-between" blockAlign="center">
                    {OFFERED_PAGE_SIZES.has(pagination.limit) ? (
                      <PageSizeSelect value={pagination.limit} onChange={handleLimitChange} />
                    ) : (
                      /* A link can carry any ?limit=. Rather than let the Select label an
                         unlisted size with the wrong number, say the real one and offer a
                         way back onto the sizes this console offers. */
                      <InlineStack gap="200" blockAlign="center">
                        <Text variant="bodySm" tone="subdued">
                          {pagination.limit} rows per page, from the link
                        </Text>
                        <Button variant="plain" onClick={() => handleLimitChange(DEFAULT_LIMIT)}>
                          Use {DEFAULT_LIMIT}
                        </Button>
                      </InlineStack>
                    )}
                    {adjustmentsPagination.totalPages > 1 && (
                      <Pagination
                        hasPrevious={adjustmentsPagination.hasPrevious}
                        onPrevious={() =>
                          setPagination(prev => ({ ...prev, page: prev.page - 1 }))
                        }
                        hasNext={adjustmentsPagination.hasNext}
                        onNext={() =>
                          setPagination(prev => ({ ...prev, page: prev.page + 1 }))
                        }
                        label={`Page ${adjustmentsPagination.page} of ${adjustmentsPagination.totalPages}`}
                      />
                    )}
                  </InlineStack>
                </Box>
                )}
            </>
          </BlockStack>
        </Box>
      </Card>

      {/* All modals would continue here - keeping same as original */}
      {/* Create/Edit Modal */}
      <Modal
        open={createModalActive || editModalActive}
        onClose={() => {
          setCreateModalActive(false);
          setEditModalActive(false);
          resetForm();
          setSelectedAdjustment(null);
        }}
        title={selectedAdjustment ? 'Edit Commission Adjustment' : 'Create Commission Adjustment'}
        primaryAction={{
          content: selectedAdjustment ? 'Update' : 'Create',
          onAction: handleSubmit,
          loading: createMutation.isPending || updateMutation.isPending,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setCreateModalActive(false);
              setEditModalActive(false);
              resetForm();
              setSelectedAdjustment(null);
            },
          },
          {
            content: 'Calculate Impact',
            onAction: handleCalculateImpact,
            loading: impactMutation.isPending,
            disabled: !formData.brandId || !formData.appliedMonth || !formData.amount,
          },
        ]}
        large
      >
        <Modal.Section>
          {submitError && (
            <Box paddingBlockEnd="400">
              <Banner
                title="Error"
                tone="critical"
                onDismiss={() => setSubmitError('')}
              >
                <p>{submitError}</p>
              </Banner>
            </Box>
          )}
          <FormLayout>
            <FormLayout.Group>
              <Select
                label="Brand"
                options={[
                  { label: 'Select brand', value: '' },
                  ...brandOptions,
                ]}
                value={formData.brandId}
                onChange={(value) => setFormData(prev => ({ ...prev, brandId: value }))}
                error={formErrors.brandId}
                disabled={brandsLoading || !!brandId}
              />

              <Select
                label="Applied Month"
                options={[
                  { label: 'Select month', value: '' },
                  ...monthOptions,
                ]}
                value={formData.appliedMonth}
                onChange={(value) => setFormData(prev => ({ ...prev, appliedMonth: value }))}
                error={formErrors.appliedMonth}
              />
            </FormLayout.Group>

            <Select
              label="Type"
              options={ADJUSTMENT_TYPES}
              value={formData.adjustmentType}
              onChange={(value) => setFormData(prev => ({ ...prev, adjustmentType: value }))}
            />

            <FormLayout.Group>
              <TextField
                label="Amount"
                value={formData.amount}
                onChange={(value) => setFormData(prev => ({ ...prev, amount: value }))}
                error={formErrors.amount}
                prefix="$"
                type="number"
                step="0.01"
                min="0"
              />

              <TextField
                label="External Reference (Optional)"
                value={formData.externalRef}
                onChange={(value) => setFormData(prev => ({ ...prev, externalRef: value }))}
                placeholder="Order #, Invoice #, etc."
              />
            </FormLayout.Group>

            <TextField
              label="Reason"
              value={formData.reason}
              onChange={(value) => setFormData(prev => ({ ...prev, reason: value }))}
              error={formErrors.reason}
              placeholder="Brief reason for this adjustment"
              maxLength={500}
            />

          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Impact Preview Modal */}
      <Modal
        open={impactModalActive}
        onClose={closeImpactPreview}
        title="Adjustment Impact Preview"
        primaryAction={{
          // "Back to adjustment" rather than "Understood": it returns you to the form you
          // were filling in, and the old label gave no hint that anything came next.
          content: 'Back to adjustment',
          onAction: closeImpactPreview,
        }}
      >
        <Modal.Section>
          {impactData && (
            <BlockStack gap="400">
              <Text variant="headingMd">Commission Impact Analysis</Text>

              <InlineStack gap="400">
                <Card>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text variant="bodyMd" tone="subdued">Current Commission</Text>
                      <Text variant="headingLg">
                        {apiClient.formatCurrency(impactData.currentCommission)}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>

                <Card>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text variant="bodyMd" tone="subdued">Proposed Adjustment</Text>
                      <Text variant="headingLg" tone={impactData.impact >= 0 ? 'success' : 'critical'}>
                        {impactData.impact >= 0 ? '+' : ''}{apiClient.formatCurrency(impactData.impact)}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>

                <Card>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text variant="bodyMd" tone="subdued">New Commission</Text>
                      <Text variant="headingLg">
                        {apiClient.formatCurrency(impactData.newCommission)}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>
              </InlineStack>

              <Banner tone="info">
                <p>This adjustment will {impactData.impact >= 0 ? 'increase' : 'decrease'} the total commission by <strong>{apiClient.formatCurrency(Math.abs(impactData.impact))}</strong> for the selected month.</p>
              </Banner>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>

      {/* Audit Trail Modal */}
      <Modal
        open={auditModalActive}
        onClose={() => {
          setAuditModalActive(false);
          setSelectedAdjustment(null);
        }}
        title={`Audit Trail - ${selectedAdjustment?.reason || 'Adjustment'}`}
        primaryAction={{
          content: 'Close',
          onAction: () => {
            setAuditModalActive(false);
            setSelectedAdjustment(null);
          },
        }}
        large
      >
        <Modal.Section>
          {auditLoading ? (
            <Spinner size="large" />
          ) : (
            <BlockStack gap="300">
              {auditTrail.map((entry, index) => (
                <Card key={index}>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Badge tone={entry.action === 'created' ? 'success' : 'info'}>
                          {entry.action.toUpperCase()}
                        </Badge>
                        <Text variant="bodySm" tone="subdued">
                          {apiClient.formatDate(entry.changedAt, 'time')}
                        </Text>
                      </InlineStack>
                      <Text variant="bodyMd">
                        {entry.admin?.name || 'System'} - {entry.reason}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>
              ))}
              {auditTrail.length === 0 && (
                <EmptyState
                  heading="No audit trail available"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Audit trail information is not available for this adjustment.</p>
                </EmptyState>
              )}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalActive}
        onClose={() => {
          setDeleteModalActive(false);
          setSelectedAdjustment(null);
          setDeleteReason('');
        }}
        title="Delete Commission Adjustment"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          onAction: handleDelete,
          loading: deleteMutation.isPending,
          // Reject already required a reason and delete did not, although both are
          // recorded in the audit trail. A deletion is the more destructive of the
          // two, so it cannot be the one with the weaker record.
          disabled: !deleteReason.trim(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setDeleteModalActive(false);
              setSelectedAdjustment(null);
              setDeleteReason('');
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text variant="bodyMd">
              Are you sure you want to delete this commission adjustment? This action cannot be undone.
            </Text>

            {selectedAdjustment && (
              <Card>
                <Box padding="300">
                  <BlockStack gap="200">
                    <Text variant="headingMd">
                      {selectedAdjustment.adjustmentType === 'ADDITION' ? '+' : '-'}
                      {apiClient.formatCurrency(selectedAdjustment.amount)}
                    </Text>
                    <Text variant="bodyMd" tone="subdued">
                      {selectedAdjustment.reason}
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            )}

            <TextField
              label="Reason for deletion"
              value={deleteReason}
              onChange={setDeleteReason}
              placeholder="Explain why this adjustment is being deleted"
              multiline={2}
              requiredIndicator
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Reject Confirmation Modal */}
      <Modal
        open={rejectModalActive}
        onClose={() => {
          setRejectModalActive(false);
          setSelectedAdjustment(null);
          setRejectReason('');
        }}
        title="Reject Commission Adjustment"
        primaryAction={{
          content: 'Reject',
          destructive: true,
          onAction: handleReject,
          loading: rejectMutation.isPending,
          disabled: !rejectReason.trim(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setRejectModalActive(false);
              setSelectedAdjustment(null);
              setRejectReason('');
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {selectedAdjustment && (
              <Card>
                <Box padding="300">
                  <BlockStack gap="200">
                    <Text variant="headingMd">
                      {selectedAdjustment.adjustmentType === 'ADDITION' ? '+' : '-'}
                      {apiClient.formatCurrency(selectedAdjustment.amount)}
                    </Text>
                    <Text variant="bodyMd" tone="subdued">
                      {selectedAdjustment.reason}
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            )}

            <TextField
              label="Reason for rejection"
              value={rejectReason}
              onChange={setRejectReason}
              placeholder="Explain why this adjustment is being rejected"
              multiline={2}
              requiredIndicator
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );

  // Render based on embedded prop. The header now lives inside mainContent's Card, so there is
  // nothing left to wrap here.
  if (embedded) {
    return mainContent;
  }

  return (
    <Page
      title={title}
      subtitle={subtitle}
      primaryAction={{
        content: 'New Adjustment',
        icon: PlusIcon,
        onAction: () => {
          resetForm();
          setCreateModalActive(true);
        },
      }}
      secondaryActions={[
        {
          content: `Pending Approvals (${pendingAdjustments.length})`,
          icon: ClockIcon,
          onAction: () => {
            setFilters(prev => ({ ...prev, approvalStatus: 'PENDING' }));
          },
          disabled: pendingAdjustments.length === 0,
        },
      ]}
      fullWidth
    >
      <Layout>
        <Layout.Section>
          {mainContent}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
